import { useIsMutating } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { parseError } from '@/lib/errors'
import { formatCurrency } from '@/lib/formatters'
import {
  ENDORSEMENT_STEP_KEY,
  EndorsementStaffAction,
  endorsementSteps,
  type InternalEndorsement,
  type QuoteInput,
  useEndorsementStep,
} from '@/models/internalEndorsement'
import { confirmStep } from './confirmStep'
import { EndorsementQuoteRow } from './EndorsementQuoteRow'

const money = (value: string | null) => (value === null ? '-' : formatCurrency(Number(value)))

function description(endorsement: InternalEndorsement, editable: boolean): string {
  if (editable) {
    return 'Enter what each carrier quoted; a credit is a minus amount. The customer sees every policy’s price at once and decides for all of them.'
  }
  if (endorsement.quotesPostedAt) return 'What each carrier quoted, as posted to the customer.'
  return 'Each policy gets a quote to fill in once the request goes to the carriers.'
}

/**
 * One quote per policy in force, posted to the customer together for one decision. Edits are kept here
 * until saved, so posting first saves them: the customer never gets prices other than the ones on screen.
 */
export function EndorsementQuotes({ endorsement }: { endorsement: InternalEndorsement }) {
  const save = useEndorsementStep(endorsementSteps.updateQuote)
  const post = useEndorsementStep(endorsementSteps.postQuotes)
  const requote = useEndorsementStep(endorsementSteps.requote)
  const approve = useEndorsementStep(endorsementSteps.approveUnchanged)
  const [drafts, setDrafts] = useState<Record<string, QuoteInput>>({})
  const [working, setWorking] = useState<'saving' | 'posting' | null>(null)
  const editable = endorsement.actions.includes(EndorsementStaffAction.EDIT_QUOTES)
  const policiesById = new Map(endorsement.policies.map((policy) => [policy.id, policy]))
  const stepping = useIsMutating({ mutationKey: ENDORSEMENT_STEP_KEY }) > 0
  const busy = working !== null || stepping
  // A draft for a quote the request no longer has, such as after asking the customer, is left behind.
  const quoteIds = new Set(endorsement.quotes.map((quote) => quote.id))
  const unsaved = Object.entries(drafts).filter(
    ([quoteId, fields]) => quoteIds.has(quoteId) && Object.keys(fields).length > 0
  )

  async function saveDrafts() {
    // One at a time, so a slower response can't put back an older price.
    for (const [quoteId, quote] of unsaved) {
      await save.mutateAsync({ id: endorsement.id, quoteId, quote })
      setDrafts(({ [quoteId]: _saved, ...rest }) => rest)
    }
  }

  async function run(step: 'saving' | 'posting', success: string) {
    setWorking(step)
    try {
      await saveDrafts()
      if (step === 'posting') await post.mutateAsync(endorsement.id)
      toast.success(success)
    } catch (error) {
      toast.error(parseError(error))
    } finally {
      setWorking(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quotes</CardTitle>
        <p className="text-sm text-neutral-600">{description(endorsement, editable)}</p>
      </CardHeader>
      {endorsement.quotes.length > 0 && (
        <CardContent className="flex flex-col gap-2">
          <fieldset disabled={busy} className="flex flex-col divide-y divide-neutral-100">
            {!editable && (
              <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 pb-2 text-xs text-neutral-500">
                <span>Policy</span>
                <span>Due now</span>
                <span>Yearly change</span>
              </div>
            )}
            {endorsement.quotes.map((quote) => (
              <EndorsementQuoteRow
                key={quote.id}
                quote={quote}
                policy={policiesById.get(quote.policyId)}
                draft={editable ? (drafts[quote.id] ?? {}) : undefined}
                onChange={
                  editable
                    ? (fields) =>
                        setDrafts((current) => ({
                          ...current,
                          [quote.id]: { ...current[quote.id], ...fields },
                        }))
                    : undefined
                }
              />
            ))}
            {!editable && (
              <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 pt-2 text-sm font-semibold">
                <span>Total</span>
                <span>{money(endorsement.proratedChangeTotal)}</span>
                <span>{money(endorsement.annualChangeTotal)}</span>
              </div>
            )}
          </fieldset>
          {editable && (
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                disabled={unsaved.length === 0 || busy}
                loading={working === 'saving' ? 'Saving…' : undefined}
                onClick={() => void run('saving', 'Saved the quotes.')}
              >
                Save
              </Button>
              {endorsement.actions.includes(EndorsementStaffAction.APPROVE_UNCHANGED) && (
                <ConfirmationDialog
                  title="Approve with no price change?"
                  description="Every policy keeps its price, so the customer has nothing to accept and the request goes straight to the bind. Prices typed here aren’t kept."
                  confirmLabel="Approve"
                  onConfirm={async () => {
                    await confirmStep(
                      approve.mutateAsync(endorsement.id),
                      'Approved with no price change. Bind it with each insurance company next.'
                    )
                  }}
                >
                  <Button type="button" variant="outline" disabled={busy}>
                    Approve with no price change
                  </Button>
                </ConfirmationDialog>
              )}
              {endorsement.actions.includes(EndorsementStaffAction.POST_QUOTES) && (
                <Button
                  type="button"
                  disabled={busy}
                  loading={working === 'posting' ? 'Posting…' : undefined}
                  onClick={() => void run('posting', 'Posted the quotes to the customer.')}
                >
                  Post quotes to customer
                </Button>
              )}
            </div>
          )}
        </CardContent>
      )}
      {endorsement.actions.includes(EndorsementStaffAction.REQUOTE) && (
        <CardContent>
          <div className="flex items-center justify-end gap-3">
            <span className="text-xs text-neutral-500">
              For new prices, such as when a quote expires or a policy starts or ends.
            </span>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              loading={requote.isPending ? 'Taking back…' : undefined}
              onClick={() =>
                requote.mutate(endorsement.id, {
                  onSuccess: () =>
                    toast.success('Took the quotes back. Enter the new prices, then post them.'),
                  onError: (error) => toast.error(parseError(error)),
                })
              }
            >
              Take quotes back
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  )
}
